import {useMemo} from 'react';

import {useMemoizedSelector} from '../../../../state/hooks';
import {usePart} from '../../../../state/views/hooks';
import type * as PanelTypes from '../../../../state/views/panel/types';
import {useDeepEqualValue} from '../../../../util/hooks';
import {
  useFullScreenLinePlotMaxRunsDynamicConfig,
  useLinePlotMaxRunsDynamicConfig,
} from '../../../../util/rampFeatureFlags/linePlotDynamicConfigs';
import {
  useRampFlagFullFidelityRunsPerChart,
  useRampFlagIncreaseFullScreenMaxRuns,
} from '../../../../util/rampFeatureFlags/linePlotFeatureFlags';
import {useIsFullScreenPanel} from '../../../Panel/hooks/useIsFullScreenPanel';
import {useDefinedMetricContext} from '../../../PanelBank/contextProviders/DefinedMetricContext';
import {setPanelStartingXAxisFromGlobMatch} from '../../../PanelBank/contextProviders/defineMetricUtils';
import {type OrganizedSettings} from '../../../PanelBank/types';
import {type RunsLinePlotConfig} from '../../../PanelRunsLinePlot/types';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {getCascadingSetting} from '../getCascadingSetting';
import {makeCascadingPanelMediaConfigSelector} from '../media/mediaSettingsSelectors';
import {getCascadingExcludeOutlierSetting} from '../runLinePlots/utils/getCascadingExcludeOutliers';
import {clampMaxRunsForLinePlot} from '../runLinePlots/utils/getCascadingMaxRuns';
import {useCascadingPointAggregationMethod} from '../runLinePlots/utils/getCascadingPointAggregation';
import {getCascadingShowLegend} from '../runLinePlots/utils/getCascadingShowLegend';
import {getCascadingSmoothingSettings} from '../runLinePlots/utils/getCascadingSmoothingSettings';
import {getCascadingXAxisSettings} from '../runLinePlots/utils/getCascadingXAxisSettings';
import {getCascadingYAxisSettings} from '../runLinePlots/utils/getCascadingYAxisSettings';
import {useSectionSettingsContextMaybe} from '../SectionSettingsContextProvider';
import {
  type DerivedPointVisualizationOption,
  isPointVizFullFidelity,
  type LinePlotSettings,
} from '../types';

/**
 * The Media Browser panel with its fully cascaded (workspace → section →
 * panel) media config, composed from the per-field cascading Redux selectors.
 * This is the panel render-path entry point for media settings; it does not
 * read from PanelContext or `inheritedSettings`.
 */
export const useMediaBrowserPanelWithCascadedConfig = (
  panelRef: PanelTypes.Ref
) => {
  const panel = usePart(panelRef);
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const isMediaBrowser = panel.viewType === 'Media Browser';
  const cascadedMediaConfig = useMemoizedSelector(
    makeCascadingPanelMediaConfigSelector,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    panelRef,
    isMediaBrowser // short circuit for non-media browser panels
  );
  const modifiedPanel = useMemo(
    () =>
      panel.viewType === 'Media Browser'
        ? {...panel, config: cascadedMediaConfig}
        : panel,
    [panel, cascadedMediaConfig]
  );
  return useDeepEqualValue(modifiedPanel);
};

/**
 * This hook determine if the parent cascading settings or panel setting
 * should be used. It only gets used in PanelCompRedux.
 *
 * Hierarchy: panelConfig >> (sectionSettings >> workspaceSettings)
 *
 * @param inheritedSettings either the workspace or section setting
 */
export const usePanelWithInheritedSettings = (
  panelRef: PanelTypes.Ref,
  inheritedSettings: OrganizedSettings | undefined
) => {
  const panel = usePart(panelRef);
  const {definedGlobs} = useDefinedMetricContext();
  const canOverrideMaxRuns = useRampFlagFullFidelityRunsPerChart();
  const canIncreaseFullScreenMaxRuns = useRampFlagIncreaseFullScreenMaxRuns();
  const dynamicConfigMaxRuns = useLinePlotMaxRunsDynamicConfig();
  const dynamicConfigFullScreenMaxRuns =
    useFullScreenLinePlotMaxRunsDynamicConfig();
  const {isFullScreen} = useIsFullScreenPanel();
  const isRunsLinePlot = panel.viewType === 'Run History Line Plot';

  const mediaBrowserPanel = useMediaBrowserPanelWithCascadedConfig(panelRef);

  const cascadingPointAggregationSettings = useCascadingPointAggregationMethod(
    inheritedSettings?.linePlot,
    isRunsLinePlot ? panel.config : undefined
  );

  const modifiedPanel = useMemo(() => {
    if (panel.viewType === 'Media Browser') {
      return mediaBrowserPanel;
    }

    if (inheritedSettings == null) {
      return panel;
    }

    // We need to pipe through useRunsTableGroupingInPanels so we can show
    // individual runs in the run comparer
    if (panel.viewType === 'Run Comparer') {
      return {
        ...panel,
        config: {
          ...panel.config,
          // section setting doesn't exist, so inherit workspace value
          useRunsTableGroupingInPanels:
            inheritedSettings.linePlot?.useRunsTableGroupingInPanels,
        },
      };
    }

    if (isRunsLinePlot) {
      const globInjectedPanelConfig = setPanelStartingXAxisFromGlobMatch(
        panel.config,
        definedGlobs
      );

      const configSettings = getLinePlotPanelConfigWithInheritedSettings(
        cascadingPointAggregationSettings.pointVisualizationMethod,
        inheritedSettings.linePlot,
        globInjectedPanelConfig
      );

      const throttledLimit = clampMaxRunsForLinePlot(
        {
          isBucketing: isPointVizFullFidelity(
            cascadingPointAggregationSettings.pointVisualizationMethod
          ),
          canOverrideMaxRuns,
          isFullScreen,
          canIncreaseFullScreenMaxRuns,
          dynamicConfigMaxRuns,
          dynamicConfigFullScreenMaxRuns,
        },
        configSettings.limit
      );

      return {
        ...panel,
        config: {
          ...configSettings,
          limit: throttledLimit,
        },
      };
    }
    return panel;
  }, [
    canIncreaseFullScreenMaxRuns,
    canOverrideMaxRuns,
    cascadingPointAggregationSettings,
    definedGlobs,
    dynamicConfigFullScreenMaxRuns,
    dynamicConfigMaxRuns,
    inheritedSettings,
    isFullScreen,
    mediaBrowserPanel,
    isRunsLinePlot,
    panel,
  ]);

  const deepEqualPanel = useDeepEqualValue(modifiedPanel);

  return deepEqualPanel;
};

/**
 * Modifies runs line plot panel config with inherited settings.
 * Panel settings should always take precedence over workspace and section settings.
 */
export const getLinePlotPanelConfigWithInheritedSettings = (
  inheritedPointVisualizationMethod: DerivedPointVisualizationOption,
  inheritedSettings: LinePlotSettings | undefined,
  panelConfig: RunsLinePlotConfig
) => {
  const newPanelConfig = {
    // Order matters here
    ...panelConfig, // settings that belong at panel level and cannot be updated at a higher level
    ...getInheritedPanelSettings(
      inheritedPointVisualizationMethod,
      inheritedSettings,
      panelConfig
    ),
  };

  // @ts-expect-error - ignoreOutliers is deprecated, make sure it doesn't show up downstream
  delete newPanelConfig.ignoreOutliers;
  // @ts-expect-error - suppressLegends is deprecated, make sure it doesn't show up downstream
  delete newPanelConfig.suppressLegends;

  return newPanelConfig;
};

const getInheritedPanelSettings = (
  inheritedPointVisualizationMethod: DerivedPointVisualizationOption,
  // This should be the resolved settings between workspace and section settings
  inheritedSettings: LinePlotSettings | undefined,
  panelSettings?: RunsLinePlotConfig
) => {
  return {
    // Workspace vs section settings are resolved at a higher level, so
    // pass undefined for child/section settings. For certain settings,
    // we apply different logic at panel settings like x-axis system metric panels.
    ...getCascadingXAxisSettings(inheritedSettings, undefined, panelSettings),
    ...getCascadingSmoothingSettings(
      inheritedSettings,
      undefined,
      panelSettings
    ),
    ...getCascadingYAxisSettings(inheritedSettings, undefined, panelSettings),
    showOriginalAfterSmoothing: getCascadingSetting([
      inheritedSettings?.showOriginalAfterSmoothing,
      panelSettings?.showOriginalAfterSmoothing,
    ]),
    singlePointRenderMode: getCascadingSetting([
      inheritedSettings?.singlePointRenderMode,
      panelSettings?.singlePointRenderMode,
    ]),
    // This setting don't exist at panel level, so just take the parent values
    colorRunNames: inheritedSettings?.colorRunNames,
    coordinateZooming: inheritedSettings?.coordinateZooming ?? false,
    // This setting don't exist at panel level, so just take the parent values
    displayFullRunName: inheritedSettings?.displayFullRunName,
    excludeOutliers: getCascadingExcludeOutlierSetting(
      inheritedPointVisualizationMethod,
      inheritedSettings,
      undefined,
      panelSettings
    ),
    fullFidelityMode: getCascadingSetting([
      inheritedSettings?.fullFidelityMode,
      panelSettings?.fullFidelityMode,
    ]),
    groupAgg: getCascadingSetting([
      inheritedSettings?.groupAgg,
      panelSettings?.groupAgg,
    ]),
    groupingDepth: inheritedSettings?.groupingDepth,
    stabilizeRunGroups: getCascadingSetting([
      inheritedSettings?.stabilizeRunGroups,
      panelSettings?.stabilizeRunGroups,
    ]),
    highlightedCompanionRunOnly: inheritedSettings?.highlightedCompanionRunOnly,
    limit: getCascadingSetting([
      inheritedSettings?.limit,
      panelSettings?.limit,
    ]),
    // This is calculated at a higher level, so just take the parent value
    pointVisualizationMethod: inheritedPointVisualizationMethod,
    showLegend: getCascadingSetting([
      getCascadingShowLegend({
        // @ts-expect-error - suppressLegends is deprecated
        suppressLegends: inheritedSettings?.suppressLegends,
        showLegend: inheritedSettings?.showLegend,
      }),
      panelSettings?.showLegend,
    ]),
    tooltipNumberOfRuns: inheritedSettings?.tooltipNumberOfRuns,
    useRunsTableGroupingInPanels: getCascadingSetting([
      inheritedSettings?.useRunsTableGroupingInPanels,
      panelSettings?.useRunsTableGroupingInPanels,
    ]),
  };
};
